import type { StoreApi } from "zustand/vanilla";
import type { VoiceAgentStore } from "@moss-js/founding-agent/core";
import { mergeTranscript } from "@moss-js/founding-agent/core";
import { CHAT_PANEL_ID, createChatState, type ChatState } from "./chat-state.js";
import { icon } from "./icons.js";

// The React ChatPanel shows only the most recent turns.
const MAX_TURNS = 6;

export interface TranscriptOpts {
  suggestedPrompts?: string[];
  /** Chat-open state shared with the pill's toggle. A private (always closed)
   *  one is used when omitted. */
  chat?: ChatState;
}

export interface TranscriptHandle {
  /** Replace the suggested-prompt chips. open()'s config-timeout fallback
   *  mounts the panel with none; a late /config supplies the real ones. */
  setPrompts(prompts: string[]): void;
  destroy(): void;
}

export function createTranscript(
  root: HTMLElement,
  store: StoreApi<VoiceAgentStore>,
  opts: TranscriptOpts,
): TranscriptHandle {
  const prompts = opts.suggestedPrompts ?? [];
  const chat = opts.chat ?? createChatState();

  // Panel: first in the bubble column (React DOM order: panel, attribution,
  // pill, so Tab runs input -> send -> pill controls); shown while connected
  // and the chat toggle is open.
  const panel = document.createElement("div");
  panel.id = CHAT_PANEL_ID;
  panel.setAttribute("data-moss-transcript", "");
  panel.hidden = true;

  // Suggested-prompt chips container
  const chipsEl = document.createElement("div");
  chipsEl.setAttribute("data-moss-prompts", "");
  panel.appendChild(chipsEl);

  // Empty-state hint, swapped for the turns once there are any.
  const hintEl = document.createElement("p");
  hintEl.setAttribute("data-moss-hint", "");
  hintEl.textContent = "Say something or type below.";
  panel.appendChild(hintEl);

  // Turns container: a scrollable live region.
  const turnsEl = document.createElement("div");
  turnsEl.setAttribute("data-moss-turns", "");
  turnsEl.setAttribute("role", "log");
  turnsEl.setAttribute("aria-live", "polite");
  turnsEl.hidden = true;
  panel.appendChild(turnsEl);

  // Typed-chat row: an input + send button at the bottom of the panel.
  const chatForm = document.createElement("form");
  chatForm.setAttribute("data-moss-chat-form", "");

  const chatInput = document.createElement("input");
  chatInput.type = "text";
  chatInput.setAttribute("aria-label", "Type a message");
  chatInput.setAttribute("data-moss-chat-input", "");
  chatForm.appendChild(chatInput);

  const chatSend = document.createElement("button");
  chatSend.type = "submit";
  chatSend.setAttribute("data-moss-chat-send", "");
  chatSend.setAttribute("aria-label", "Send message");
  chatSend.replaceChildren(icon("send-horizontal"));
  chatForm.appendChild(chatSend);

  panel.appendChild(chatForm);

  root.prepend(panel);

  // Local draft tracking: mirrors the React ChatPanel's useState(draft). The
  // send button is also disabled on an empty draft, so typing needs to update
  // it without going through renderChatInput (which only reacts to
  // sendChatMessage/intakeOpen and must not rebuild on every keystroke).
  let draft = "";

  function updateSendDisabled(state: ReturnType<typeof store.getState>): void {
    chatSend.disabled = !state.sendChatMessage || state.intakeOpen || draft.trim().length === 0;
  }

  chatInput.addEventListener("input", () => {
    draft = chatInput.value;
    updateSendDisabled(store.getState());
  });

  chatForm.addEventListener("submit", (e) => {
    e.preventDefault();
    const state = store.getState();
    const text = chatInput.value.trim();
    if (!text || !state.sendChatMessage || state.intakeOpen) return;
    state.sendChatMessage(text);
    chatInput.value = "";
    draft = "";
    updateSendDisabled(state);
  });

  function renderChatInput(state: ReturnType<typeof store.getState>): void {
    const disabled = !state.sendChatMessage || state.intakeOpen;
    chatInput.disabled = disabled;
    chatInput.placeholder = state.sendChatMessage ? "Type a message" : "Connecting...";
    updateSendDisabled(state);
  }

  // Chips are rebuilt wholesale by buildChips (at mount and on setPrompts)
  // and only toggled disabled in place afterwards. Labelled with their text
  // for screen readers.
  function buildChips(prompts: string[]): void {
    chipsEl.replaceChildren();
    for (const prompt of prompts) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.setAttribute("data-moss-prompt", "");
      btn.setAttribute("aria-label", prompt);
      btn.textContent = prompt;
      btn.addEventListener("click", () => {
        const state = store.getState();
        if (!state.intakeOpen) state.sendChatMessage?.(prompt);
      });
      chipsEl.appendChild(btn);
    }
  }

  function renderChips(state: ReturnType<typeof store.getState>): void {
    const disabled = state.sendChatMessage === null || state.intakeOpen;
    const buttons = chipsEl.querySelectorAll<HTMLButtonElement>("[data-moss-prompt]");
    for (const btn of buttons) btn.disabled = disabled;
  }

  buildChips(prompts);

  function renderTurns(state: ReturnType<typeof store.getState>): void {
    const { streamingTranscripts, transcript, chatPrompts } = state;
    const displayItems = mergeTranscript(transcript, streamingTranscripts, chatPrompts).slice(-MAX_TURNS);

    hintEl.hidden = displayItems.length > 0;
    turnsEl.hidden = displayItems.length === 0;

    // Auto-scroll only when the user is already near the bottom, so we don't
    // yank them away while they've scrolled up to read earlier turns.
    const nearBottom =
      turnsEl.scrollHeight - turnsEl.scrollTop - turnsEl.clientHeight < 40;

    // Update child nodes in place: turnsEl is an aria-live log, so clearing and
    // re-appending every turn would re-announce the whole transcript on each
    // streaming update. Only changed nodes are touched.
    const children = turnsEl.children;
    for (let i = 0; i < displayItems.length; i++) {
      const item = displayItems[i];
      let el = children[i] as HTMLElement | undefined;
      if (!el) {
        el = document.createElement("div");
        turnsEl.appendChild(el);
      }
      if (el.getAttribute("data-moss-turn") !== item.role) el.setAttribute("data-moss-turn", item.role);
      if (el.textContent !== item.text) el.textContent = item.text;
      el.toggleAttribute("data-pending", !item.isFinal);
    }
    while (children.length > displayItems.length) turnsEl.removeChild(turnsEl.lastChild!);
    if (nearBottom) turnsEl.scrollTop = turnsEl.scrollHeight;
  }

  function renderVisibility(state: ReturnType<typeof store.getState>): void {
    panel.hidden = !(state.connectionState === "connected" && chat.open);
  }

  // Gate turn rebuilds: the store fires on every write (audio levels ~25Hz),
  // but turns only change when transcript/streaming/chatPrompts change.
  let prevTranscript = store.getState().transcript;
  let prevStreaming = store.getState().streamingTranscripts;
  let prevChatPrompts = store.getState().chatPrompts;
  let prevSendChat = store.getState().sendChatMessage;
  let prevIntakeOpen = store.getState().intakeOpen;
  let prevConn = store.getState().connectionState;

  renderVisibility(store.getState());
  renderChips(store.getState());
  renderTurns(store.getState());
  renderChatInput(store.getState());

  const unsubChat = chat.subscribe(() => renderVisibility(store.getState()));

  const unsub = store.subscribe((state) => {
    if (state.connectionState !== prevConn) {
      prevConn = state.connectionState;
      renderVisibility(state);
    }
    if (state.sendChatMessage !== prevSendChat || state.intakeOpen !== prevIntakeOpen) {
      renderChips(state);
      prevSendChat = state.sendChatMessage;
      prevIntakeOpen = state.intakeOpen;
      renderChatInput(state);
    }
    if (
      state.transcript !== prevTranscript ||
      state.streamingTranscripts !== prevStreaming ||
      state.chatPrompts !== prevChatPrompts
    ) {
      prevTranscript = state.transcript;
      prevStreaming = state.streamingTranscripts;
      prevChatPrompts = state.chatPrompts;
      renderTurns(state);
    }
  });

  return {
    setPrompts(next) {
      buildChips(next);
      renderChips(store.getState());
    },
    destroy() {
      unsub();
      unsubChat();
      panel.remove();
    },
  };
}
